/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv } from 'mobx-state-tree';

import StatsProcessor from './StatsProcessor';

import StringResources from 'nls/StringResources';

import WidgetContextChecker from 'features/compare/WidgetContextChecker';

import CompareTableAPI from 'api/CompareTableAPI';

export const CalcType = types.string;

const ChartInfo = types.model({
	widgetId: '',
	widgetAlias: '',
	measureName: '',
	measureLabel: '',
	measureList: types.frozen()
});



export function getWidgetInfoList( statsModelMap, widgetAliasMap ) {
	let widgetAliasStart = 65;
	return Array.from(statsModelMap.entries()).map( (entry) => {
		return { widgetId: entry[0], widgetAlias: widgetAliasMap && widgetAliasMap[entry[0]] || String.fromCharCode(widgetAliasStart++)  };
	});
}

/*
 * @return {Array.<Object>} Object is of the form
 * { measureName :<string>, metadata: <object> }
 */
export function getMeasuresList(statsModelMap, widgetId, widgetContentMap) {
	const statsModel = statsModelMap.get(widgetId);
	const measuresMap = statsModel.measures || new Map();
	const measures = [];
	measuresMap.forEach( (measureDetail, measureName) => {
		measures.push( { measureName : measureName, metadata : measureDetail.metadata || measureDetail.get('metadata') } );
	});
	if (measures.length < 2) {
		return measures;
	}
	const widgetContent =  widgetContentMap && widgetContentMap[widgetId];
	if (!widgetContent) {
		return measures;
	}
	return sortMeasuresBySlots(measures, widgetContent);
}

export function sortMeasuresBySlots(measures, widgetContent) {
	const visualization = widgetContent.getFeature('Visualization');
	if(visualization) {
		const sortedMeasures = [];
		const measureUniqueIds = measures.map(measure => measure.metadata.itemUniqueId);
		const mapInfos = visualization.getSlots().getMappingInfoList();
		for (let i = 0; i < mapInfos.length; ++i) {
			const indexOfMeasure = measureUniqueIds.indexOf(mapInfos[i].dataItem.getId());
			if (indexOfMeasure > -1) {
				sortedMeasures.push( { measureName : measures[indexOfMeasure].measureName, metadata : measures[indexOfMeasure].metadata } );
			}
		}
		return sortedMeasures;
	}
	return measures;
}

const WidgetContextProcessState = types.enumeration('WidgetContextProcessState', ['pending', 'ready', 'error']);

const CompareTableSummary = types.model({
	applicableCalcs: types.union( types.array( CalcType ), types.undefined ),
	charts: types.union( types.array( ChartInfo ), types.undefined ),
	updated: 0,
	widgetContextProcessState: types.optional( WidgetContextProcessState, 'pending' )
}).volatile(() => ({
	theStatsProcessor: StatsProcessor,
	combinedSummaryValueMap: {},
	theWidgetContextChecker: WidgetContextChecker,
	getApplicableCalcTypes: StatsProcessor.getApplicableSummaryCalcTypes,
	widgetMeasureNameMap: {},	//A map of widgetId -> measureName (owned by compareModel - shared by compareTableSummary and compareTableSelectionSummary)
	widgetContextCheckResult: {},
	widgetDataVersions: {},	//A map by id that tracks when widget data changes or is recomputed (computeSummaryInfo/recomputeSummaryInfo)
	fnSpyNewStats: null,	//A function that uses widgetDataVersions to track changes to stats (and potentially resolve the waitForNewStats promise)
	fnSpyNewCalcs: null
})).views((self) => ({
	getNewCompareTableStatsEnabled() {
		const env = getEnv(self);
		return env && env.isFeatureEnabled && !env.isFeatureEnabled('newCompareTableStatsEnabled', 'false');
	},

	/**
	 * @returns the compareTableAPI object for this compare table summary model.
	 */
	getAPI() {
		return self._api;
	},

	/*
	 * @param {number} row row index into applicableCalcs
	 * @param {object} chartStats chart summary stats object that holds all calculation results
	 * @return calculation result
	 */
	getData(row, chartStats) {
		const data = { value: '' };

		const calc = self.applicableCalcs[row];
		if( calc && chartStats ) {
			data.value = chartStats.stats && chartStats.stats[calc] || '';
			data.valueUse = chartStats.statsUse && chartStats.statsUse[calc] || '';
			data.toolTip = chartStats.toolTip && chartStats.toolTip[calc];
			data.formatSpec = chartStats.meta && chartStats.meta.format && chartStats.meta.format || '';

			//If the dataset has negative values, apply an indicator to any calculations in the set listed (eg: TOTAL_SEL and PERCENT_SELvsALLDATA for selections.)
			const showHasNegativeValuesIndicatorCalcs = chartStats.statsUse && chartStats.statsUse.hasNegativeValues;
			if (showHasNegativeValuesIndicatorCalcs && showHasNegativeValuesIndicatorCalcs.indexOf(calc) >= 0) {
				data.hasNegativeValuesIndicator = ' *';
			}
		}
		return data;
	},

	/**
	 * @param widgetId - the widgetId of interest.
	 * @returns the selected measure name for the given widgetId
	 */
	getSelectedMeasureName(widgetId) {
		return self.widgetMeasureNameMap[widgetId] || null;
	},

	/*
	 * @param {object} chartStatsList list of chartStats (array has 1 entry for each widget on compare card)
	 *
	 * @return combined calculation results
	 * 			i.e. given stats for widget A, B, return combined calculations for A and B
	 */
	getCalculatedData(chartStatsList) {
		self.fnSpyNewStats && self.fnSpyNewStats(chartStatsList);

		const params = {
			statsInfoList : chartStatsList,
			applicableCalcs : self.applicableCalcs,
			getString : StringResources.get.bind(StringResources),
			widgetContextCheckResult: self.widgetContextCheckResult,
			newCompareTableStatsEnabled: self.getNewCompareTableStatsEnabled(),
			combinedSummaryValueMap: self.combinedSummaryValueMap
		};

		if (chartStatsList.length < 2) {
			self.setCombinedSummaryValues(null);
		}

		if(self.widgetContextProcessState === 'ready' || chartStatsList.length < 2) {
			const calculatedData = self.theStatsProcessor.getCalculatedSummaryData( params );
			self.fnSpyNewCalcs && self.fnSpyNewCalcs(calculatedData);
			return calculatedData;
		} else {
			if( self.widgetContextProcessState === 'pending' && !self._checker) {
				self._checker = new self.theWidgetContextChecker(self.widgetContentMap);
				//TODO use mobx utils
				const isSelectionStats = !!chartStatsList[0].statsUse.numSelections;
				self._checker.process(params.newCompareTableStatsEnabled, isSelectionStats).then( (result) => {
					params.combinedSummaryValueMap = result.combinedSummaryResponse && result.combinedSummaryResponse.measures;
					self.setCombinedSummaryValues(params.combinedSummaryValueMap);
					if (self._checker) {
						self.setWidgetContextProcessInfo('ready', result);
						self._checker = null;
						self._processedCheck && self._processedCheck(); //for unit testing
					}
				});
			}
			return self.theStatsProcessor.getCalculatedSummaryData( params );
		}
	},

	/**
	 * @returns the widgetDataVersions for each widget alias in the comparison eg: { 'A': 3, 'B': 1 }
	 * 			how many times the summary column data has changed (by computeSummaryInfo or recomputeSummaryInfo)
	 */
	getWidgetDataVersions() {
		return self.widgetDataVersions;
	},

	/**
	 * @returns true if calculations are in a ready state.
	 */
	areCalculationsReady() {
		return self.widgetContextProcessState === 'ready';
	}
})).actions(self => ({
	afterCreate() {
		self._api = new CompareTableAPI(self);
	},

	beforeDestroy() {
		self._api.destroy();
		self._api = null;
		self._checker = null;
	},

	/**
	 * This function allows a caller to "spy on" updates to widget data in the compare table.
	 * BACKGROUND:
	 * There are 2 phases to ensuring data in the compare table has been updated
	 * Phase1 is driven by having new data in one or both widgets (computeSummaryInfo) (updateData/updateSelectionData)
	 * 					OR user changing the focus measure (recomputeSummaryInfo)
	 * The second phase (driven by REACT rendering) is getCalculatedData which is called when the stats have been processed
	 * and the combined column is being rendered (getCalculatedData)
	 * NOTE: Since getting data for the compareTable/calculatedData can execute queries that are independent 
	 * of the widget render sequence, the best way to test widget/calculated data is available is to
	 * spy on those updates
	 * @param {function} fnSpyNewStats - a function which will be invoked when stats for widgets have data (possibly unchanged)
	 * 									Use: getWidgetDataVersions() to determine the state of each widget.
	*/
	setSpyNewStats(fnSpyNewStats) {
		self.fnSpyNewStats = fnSpyNewStats;
	},

	/**
	 * This function allows a caller to "spy on" requests to get calculation data.
	 * @param {function} fnSpyNewCalcs - a function which will be invoked when calculations are requested.
	 * 									Use: areCalculationsReady() to determine if they're up-to-date.
	*/
	setSpyNewCalcs(fnSpyNewCalcs) {
		self.fnSpyNewCalcs = fnSpyNewCalcs;
	},

	/**  
	 * This function is used to count and store the number of times each widget in this summary
	 * has been computed (ie: data changed) (via computeSummaryInfo or recomputeSummaryInfo)
	 * @param {string} widgetAlias - 'A' or 'B' for the current compare card but could be any alias.
	 * Examples:
	 * initial - {}.  
	 * computeSummaryInfo on A - { 'A': 1 }, 	- from updateData or updateSelectionData
	 * computeSummaryInfo on B - { 'A':1, 'B':1 }
	 * recomputeSummaryInfo on B - { 'A':1, 'B':2 } - user changes the measure selector on B.
	*/
	updateWidgetDataVersion(widgetAlias) {
		if (widgetAlias) {
			if (!self.widgetDataVersions[widgetAlias]) {
				self.widgetDataVersions[widgetAlias]=0;
			}
			self.widgetDataVersions[widgetAlias]++;
		}
	},

	setCombinedSummaryValues(values) {
		self.combinedSummaryValueMap = values;
	},

	setStatsProcessor(processor) {
		self.theStatsProcessor = processor;
	},

	setWidgetContextChecker( checker ) {
		self.theWidgetContextChecker = checker;
	},

	setWidgetContextProcessInfo( state, result ) {
		self.widgetContextCheckResult = result;
		self.widgetContextProcessState = state;
	},

	/**
	 * called by compareModel to establish a map of selected measures by widgetId.
	 * @param widgetMeasureNameMap - a map of widgetId - measureName
	 * (shared by compareTableSummary and selectedCompareTableSummary)
	 */
	setWidgetMeasureNameMap( widgetMeasureNameMap ) {
		self.widgetMeasureNameMap = widgetMeasureNameMap;
	},

	/*
	 * populates Model based on provided stats models
	 * if modelType === 'selections', sets it up for selections
	 *
	 * @param { object } statsModelMap - the current stats models
	 * @param { object } widgetAliasMap - a map of widgetId to alias (ie: 'A' or 'B')
	 * @param { object } widgetContentMap - a map of widgetId to widgetApi
	 * @param { string } widgetId - the widgetId being updated (ie: this function is alled by updateData/updateSelectionData)
	 * @param { string } modelType - one of 'selections' or 'summary' depending on the type of model.
	 * populating model entails creating a 'frame of reference' where self.charts & self.applicableCalcs
	 * are set in a way that makes sense for a summary view
	 * 		self.charts to indicate measure considered for each widget (+ additional info)
	 *		self.applicableCalcs list the applicable calculations to show given the set of measures from the 2 widgets
	 */
	computeSummaryInfo(statsModelMap, widgetAliasMap, widgetContentMap, widgetId, modelType ) {
		const newCompareTableStatsEnabled = self.getNewCompareTableStatsEnabled();
		const summary = {
			applicableCalcs: [],
			charts: [] //contains {widgetId, widgetAlias, measureName, measureList}, {widgetId, widgetAlias, measureName}
		};
		self.widgetContentMap = widgetContentMap;
		self.widgetContextProcessState = 'pending';

		widgetAliasMap = widgetAliasMap || {};
		const widgets = getWidgetInfoList(statsModelMap, widgetAliasMap);

		//The 'primary widget' is the first by default.
		const primary = widgets && widgets[0];

		//If the primary widget has a list of measures, check if the measure has been set (by recompute)
		//otherwise, consider the first measure of the primary widget as the primary measure
		const currentPrimaryChartMeasureName = primary && self.getSelectedMeasureName(primary.widgetId);
		const primaryMeasures = primary && getMeasuresList(statsModelMap, primary.widgetId, self.widgetContentMap);
		const primaryMeasure = primaryMeasures &&
			(primaryMeasures.find(measureInfo => measureInfo.measureName === currentPrimaryChartMeasureName)
			|| primaryMeasures[0]);
		//Synchronize the widgetMeasureName to ensure data changes work.
		if (primaryMeasure) {
			self.widgetMeasureNameMap[primary.widgetId] = primaryMeasure.measureName;
		}

		const measuresToCheck = primaryMeasure && [ primaryMeasure ] || [];

		const restOfTheWidgets =  widgets.filter( (w) => {
			return w.widgetId !== primary.widgetId;
		} );

		const otherWidgetInfo = restOfTheWidgets.map( (otherWidget) => {
			const otherWidgetMeasures = otherWidget && getMeasuresList(statsModelMap, otherWidget.widgetId, self.widgetContentMap) || [];
			//If this widget has a list of measures, check if the measure has been set (by recompute)
			//next, try to set the measure to match the primary widget measure.
			//If neither are available, consider the first measure of the widget as the selected measure
			const otherWidgetMeasureName = self.getSelectedMeasureName(otherWidget.widgetId);
			const otherWidgetSelectedMeasure = otherWidgetMeasures.find(measureInfo => measureInfo.measureName === otherWidgetMeasureName);
			const otherWidgetMeasure = otherWidgetSelectedMeasure ||
				(primaryMeasure && otherWidgetMeasures.find(measureInfo => measureInfo.measureName === primaryMeasure.measureName))
				|| otherWidgetMeasures[0];
			//Synchronize the widgetMeasureName to ensure data changes work.
			if (otherWidgetMeasure) {
				self.widgetMeasureNameMap[otherWidget.widgetId] = otherWidgetMeasure.measureName;
			}

			let matchingMeasure = otherWidgetMeasure && otherWidgetMeasures.find( (m) => {
				return m.measureName === otherWidgetMeasure.measureName;
			} );
			matchingMeasure = matchingMeasure || otherWidgetMeasures.length>0 && otherWidgetMeasures[0];
			matchingMeasure && measuresToCheck.push(matchingMeasure);

			return { otherWidget, matchingMeasure, otherWidgetMeasures };
		} );

		//measuresToCheck is a list of all of the selected measures in the comparison table. Find out what calculations apply.
		const fn = modelType && modelType === 'selections' ? 'getApplicableSelectionSummaryCalcTypes' : 'getApplicableSummaryCalcTypes';
		self.getApplicableCalcTypes = fn;
		summary.applicableCalcs = self.theStatsProcessor[fn]( measuresToCheck, newCompareTableStatsEnabled ) || [];

		if(primary && primaryMeasure) {
			summary.charts.push( {
				widgetId: primary.widgetId,
				widgetAlias: primary.widgetAlias,
				measureName: primaryMeasure.measureName,
				measureLabel: primaryMeasure.metadata.label,
				measureList : primaryMeasures
			} );
		}

		otherWidgetInfo.forEach( (widgetInfo) => {
			const otherWidget = widgetInfo.otherWidget;
			if( otherWidget && widgetInfo.matchingMeasure ) {
				summary.charts.push( {
					widgetId: otherWidget.widgetId,
					widgetAlias: otherWidget.widgetAlias,
					measureName: widgetInfo.matchingMeasure.measureName,
					measureLabel: widgetInfo.matchingMeasure.metadata.label,
					measureList : widgetInfo.otherWidgetMeasures
				} );
			}
		});

		self.applicableCalcs = summary.applicableCalcs;
		self.charts = summary.charts;

		//Keep track of a "widgetDataVersion" for widgets so we can track changes.
		const widgetAlias=widgetAliasMap[widgetId];
		self.updateWidgetDataVersion(widgetAlias);
	},

	/*
	 * computeSummaryInfo() provided the initial/default summary view
	 * when user changes the measure for a widget in the summary view, recomputeSummaryInfo() figures out
	 * the a new list of applicable calculations
	 * @param { object } newChartInfo
	 * @param { string } newChartInfo.widgetId - the widgetId
	 * @param { string } measureName - a unique id for the measure, (currently: itemId_aggregation_layer)
	 */
	recomputeSummaryInfo(newChartInfo) {
		const currentSummaryInfo = self;
		const summary = {
			applicableCalcs: [],
			charts: [] //contains {widgetId, widgetAlias, measureName}, {widgetId, widgetAlias, measureName}
		};

		//update summary.charts to include baseChart into currentCharts
		//user picked one measure in one chart, find summary.applicableCalcs
		const { widgetId, measureName } = newChartInfo;
		summary.charts = JSON.parse( JSON.stringify( currentSummaryInfo.charts )); //copy
		const index = summary.charts.findIndex( (entry) => {
			return entry.widgetId === widgetId;
		} );

		const chart = summary.charts[index];
		chart.measureName = measureName;
		const measure = chart.measureList.find( e => e.measureName === measureName);
		chart.measureLabel = measure.metadata.label;

		const measuresToCheck = [measure];

		const restOfTheWidgets =  summary.charts.filter( (w) => {
			return w.widgetId !== widgetId;
		} );

		restOfTheWidgets.map( (otherWidget) => {
			const otherWidgetMeasures = otherWidget.measureList || [];
			let matchingMeasure = otherWidgetMeasures.find( (m) => {
				return m.measureName === otherWidget.measureName;
			} );
			matchingMeasure = matchingMeasure || otherWidgetMeasures.length>0 && otherWidgetMeasures[0];
			matchingMeasure && measuresToCheck.push(matchingMeasure);

			return { otherWidget, matchingMeasure, otherWidgetMeasures };
		} );

		const fn = self.getApplicableCalcTypes;

		const newCompareTableStatsEnabled = self.getNewCompareTableStatsEnabled();
		summary.applicableCalcs = self.theStatsProcessor[fn]( measuresToCheck, newCompareTableStatsEnabled ) || [];

		self.applicableCalcs = summary.applicableCalcs;
		self.charts = summary.charts;

		//Update the measure name for the widget we just changed.
		self.widgetMeasureNameMap[widgetId] = measureName;

		//Keep track of a "widgetDataVersion" for widgets so we can track changes.
		self.updateWidgetDataVersion(chart.widgetAlias);
	}
}));

export default CompareTableSummary;



// WEBPACK FOOTER //
// ./src/js/features/compare/CompareTableSummary.js